/* eslint-disable max-lines */
import { PageId } from 'constants/pages'
import { IStripeAccount } from 'models/common.model'
import {
  IEventLogger,
  IEventLogParams,
  TQuestionPageParams,
  TUpsellPageParams,
} from 'models/events.model'
import { PaymentMethod, PaymentSystem } from 'nove8/constants/payment'
import { getCampaignId } from 'nove8/helpers/getCompaignId'
import { getMobileOperatingSystem } from 'nove8/helpers/getMobileOperatingSystem'
import { EventLoggerInstanceName, LoginMethod } from 'root-constants'

export const enum ScreenName {
  SALES = 'offer',
  CANCEL = 'cancel_offer',
  UPSELL = 'pdf_zen_course_upsell',
  UPSELL_V2 = 'pdf_zen_course_upsell_v2',
  INAPP_UPSELL_FEEDING_GUIDE = 'inapp_upsell_feeding_guide',
  INAPP_UPSELL_EMOTIONAL_SUPPORT = 'inapp_upsell_emotional_support',
  CHAT_UPSELL = 'chat_upsell',
  SUB_UPSELL_CHAT_2 = 'sub_upsell_chat_2',
  SUB_UPSELL_CHAT_4 = 'sub_upsell_chat_4',
  INAPP_UPSELL_CHAT_4 = 'inapp_upsell_chat_4',
  SUB_UPSELL_CHAT_5 = 'sub_upsell_chat_5',
  INVOXIA_UPSELL = 'invoxia_upsell',
  INVOXIA_DOG_TRACKER_UPSELL = 'invoxia_dog_tracker_upsell',
  UPSELL_3_PDF_SCREEN = 'inapp_upsell_3pdf_screen',
}

export const enum Events {
  SESSION_STARTED = 'session_start',
  SEGMENT_ACTIVATED = 'segment_activated', // required for Rudderstack by initializing Firebase Remote Config
  SALE_SCREEN_SHOW = 'plans_page_show', // required for Amplitude events
  ONBOARDING_PAGE_COMPLETED = 'onboarding_page_completed',
  UPSELL_PAGE_COMPLETED = 'upsell_page_completed',
  EMAIL_PAGE_SHOW = 'email_page_show',
  EMAIL_PAGE_COMPLETED = 'email_page_completed', // required for Amplitude events
  EMAIL_PAGE_ERROR = 'email_page_error',
  PAYMENT_METHOD_PAGE_SHOW = 'payment_method_page_show', // required for Amplitude events
  PURCHASE_SHOW = 'subs_purchase_show',
  PURCHASE_STARTED = 'subs_purchase_started', // required for Amplitude events
  PURCHASE_COMPLETED = 'subs_purchase_completed', // required for Amplitude events
  PURCHASE_FAILED = 'subs_purchase_failed', // required for Amplitude events
  SUBS_PURCHASE_CLOSE = 'subs_purchase_screen_close', // required for Amplitude events
  CREATE_ACCOUNT_PAGE = 'create_account_page', // required for Amplitude events
  LOGIN_METHOD_SELECTED = 'login_method_selected', // required for Amplitude events
  ACCOUNT_CREATED = 'account_created', // required for Amplitude events
  ACCOUNT_CREATION_FAILED = 'account_creation_failed', // required for Amplitude events
  FINISH_ACCOUNT_SCREEN_VIEW = 'finish_account_screen_view', // required for Amplitude events
  DOWNLOAD_BTN_PRESSED = 'download_button_press', // required for Amplitude events
  WEBAPP_BTN_PRESSED = 'webapp_redirect_button_press', // required for Amplitude events
  VIEW_PRICE = 'prices_viewed', // required for Amplitude events
  AB_SEGMENT = 'ab_segment',
  PRESS_UPSELL_TOOLTIP = 'press_upsell_tooltip',
  INAPP_PURCHASE_COMPLETED = 'inapp_purchase_completed',
  CALENDLY_SESSION_BOOKED = 'session_booked',
  PAGE_SHOWED = 'page_showed',
  SUPPORT_EMAIL_PRESSED = 'support_mail_pressed',
  SUPPORT_BTN_CLICKED = 'support_btn_clicked',
  SEGMENT = 'segment',
  EMAIL_POPUP_SHOW = 'email_popup_show',
  EMAIL_EXTRA_SCREEN_SHOW = 'email_extra_screen_show',
  EDIT_BTN_CLICKED = 'edit_btn_clicked',
  UPSELL_OFFER_SHOWN = 'upsell_offer_shown',
  BREEDID_SCREEN_END = 'breedID_instructions_screen_end',
  BREEDID_FILE_UPLOAD = 'breedID_file_upload',
  BREEDID_PROCESSING_END = 'breedID_processing_screen_end',
  ERROR_ALERT_END = 'error_alert_end',
  BREEDID_RESULTS_SCREEN_END = 'breedID_results_screen_end',
  WS_TOOLTIP_OPND = 'ws_tooltip_opnd',
  ZENDESK_CHAT_SHOWN = 'zendesk_chat_shown',
  ZENDESK_CHAT_MSG_SENT = 'zendesk_chat_msg_sent',
  SUPPORT_BTN_CLICK = 'support_btn_click',
  EARLY_EXIT = 'early_exit',
  WOOFZ_CHAT_TAP = 'woofz_chat_tap',
  CHAT_OPENED = 'chat_opened',
  CHAT_CLOSED = 'chat_closed',
  WOOFY_LOADER_SHOW = 'woofy_loader_show',
  CLOSE_ATTEMPTED_POPUP_SHOW = 'close_attempted_popup_show',
  CLOSE_ATTEMPTED_POPUP_CLOSE = 'close_attempted_popup_close',
  COACHING_ONBOARDING_SCREEN_LOAD = 'coaching_onboarding_screen_load',
  ROVER_PAGE_SHOW = 'rover_page_show',
  CLICK_COPY_CODE = 'click_copy_code',
  ROVER_PAGE_COMPLETED = 'rover_page_completed',
  COACH_SCREEN_SHOW = 'coach_screen_show',
  COACH_OPTION_TAPPED = 'coach_option_tapped',
  PROCESSING_PURCHASE_SHOW = 'processing_purchase_show',
  SUCCESS_PURCHASE_SHOW = 'success_purchase_show',
  SUCCESS_PURCHASE_CLOSED = 'success_purchase_closed',
  BRAND_LOGO_CLICK = 'brand_logo_click',
}

class EventLoggerService {
  private loggers?: Map<EventLoggerInstanceName, IEventLogger>
  private eventsQueue: IEventLogParams[] = []
  private commonEventProps: Record<string, unknown> = {}
  private campaignId: string | null = null

  init(
    loggers: IEventLogger[],
    utmTags: Record<string, string>,
    commonEventProps: Record<string, unknown>,
  ): void {
    const entriesArr = loggers.map(
      (logger) =>
        [logger.name, logger] as [EventLoggerInstanceName, IEventLogger],
    )
    this.loggers = new Map(entriesArr)
    this.commonEventProps = commonEventProps
    this.campaignId = getCampaignId(utmTags)
  }

  addLoggers(loggers: IEventLogger[]): void {
    loggers.forEach((logger) => {
      this.loggers?.set(logger.name, logger)
    })
  }

  hasLogger(name: EventLoggerInstanceName): boolean {
    return !!this.loggers?.has(name)
  }

  logSessionStarted = ({
    cohort,
    abVariant,
    abSegmentName,
  }: {
    cohort: string
    abVariant?: string | string[]
    abSegmentName?: string | string[]
  }): void => {
    const event = Events.SESSION_STARTED
    const commonProps = {
      ...this.commonEventProps,
      attribution: {
        ...(this.commonEventProps?.attribution ?? {}),
        ...(this.campaignId && { campaign_id: this.campaignId }),
      },
    }

    const eventProperty = {
      device_type: getMobileOperatingSystem(),
      ...(abVariant && { ab_variant: abVariant }),
      ...(abSegmentName && { ab_segment_name: abSegmentName }),
      viewport_width: window.screen.width,
      viewport_height: window.screen.height,
      viewport_size: `${window.screen.width}x${window.screen.height}`,
      cohort,
      ...commonProps,
    }

    this.logEvent({ event, eventProperty })
    this.executeAndClearEventsQueue()
  }

  logQuestion({
    question,
    answers,
    pageNumber,
    customProperties = {},
    commandsLevel,
  }: TQuestionPageParams): void {
    const event = Events.ONBOARDING_PAGE_COMPLETED
    const eventProperty = {
      page_number: pageNumber,
      question,
      ...(answers && {
        answer: !Array.isArray(answers) ? answers.split(',') : answers,
      }),
      ...(commandsLevel && { level: commandsLevel }),
      ...this.commonEventProps,
      ...customProperties,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logEarlyExit({ pageNumber }: { pageNumber: number }): void {
    const event = Events.EARLY_EXIT
    this.logEventOrPushToQueue({
      event,
      eventProperty: { page_number: pageNumber, ...this.commonEventProps },
    })
  }

  logCoachingOnboardingScreenLoad({
    pageNumber,
    pageId,
  }: {
    pageNumber: number
    pageId: PageId | null
  }): void {
    const event = Events.COACHING_ONBOARDING_SCREEN_LOAD
    this.logEventOrPushToQueue({
      event,
      eventProperty: {
        page_number: pageNumber,
        page_id: pageId,
        ...this.commonEventProps,
      },
    })
  }

  // Identify Breed
  logBreedIdInstructionsScreenEnd(): void {
    const event = Events.BREEDID_SCREEN_END
    this.logEventOrPushToQueue({
      event,
      eventProperty: this.commonEventProps,
    })
  }

  logBreedIdFileUpload(): void {
    const event = Events.BREEDID_FILE_UPLOAD
    this.logEventOrPushToQueue({
      event,
      eventProperty: this.commonEventProps,
    })
  }

  logBreedIdProcessingEnd({ result }: { result: string }): void {
    const event = Events.BREEDID_PROCESSING_END
    this.logEventOrPushToQueue({
      event,
      eventProperty: { result, ...this.commonEventProps },
    })
  }

  logBreedIdResultsEnd({ result }: { result: string }): void {
    const event = Events.BREEDID_RESULTS_SCREEN_END
    this.logEventOrPushToQueue({
      event,
      eventProperty: { result, ...this.commonEventProps },
    })
  }

  logBreedsIdErrorAlertEnd({
    result,
    text,
  }: {
    result: string
    text: string
  }): void {
    const event = Events.ERROR_ALERT_END
    this.logEventOrPushToQueue({
      event,
      eventProperty: { result, text, ...this.commonEventProps },
    })
  }

  logUpsellOfferShown({ productName }: { productName: string }): void {
    const event = Events.UPSELL_OFFER_SHOWN
    const eventProperty = {
      product_name: productName,
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logUpsellComplete({
    pageNumber,
    productName,
    isInterested,
    promoDiscount,
    country,
    coupon,
  }: TUpsellPageParams): void {
    const event = Events.UPSELL_PAGE_COMPLETED
    const defaultEventProperty = {
      page_number: pageNumber,
      product_name: productName,
      viewport_width: window.screen.width,
      viewport_height: window.screen.height,
      viewport_size: `${window.screen.width}x${window.screen.height}`,
    }
    const pitPatEventProperty = {
      is_interested: isInterested,
      promo_discount: promoDiscount,
      country,
      ...(coupon && { coupon }),
      ...defaultEventProperty,
    }
    const eventProperty =
      productName === 'pitpat_activity_monitor'
        ? pitPatEventProperty
        : defaultEventProperty

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logEditBtnClicked({ productName }: { productName: string }): void {
    const event = Events.EDIT_BTN_CLICKED
    const eventProperty = {
      product_name: productName,
      viewport_width: window.screen.width,
      viewport_height: window.screen.height,
      viewport_size: `${window.screen.width}x${window.screen.height}`,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logSupportButtonClicked({
    pageNumber,
    destination,
  }: {
    pageNumber: number | null
    destination: string
  }): void {
    const event = Events.SUPPORT_BTN_CLICK
    const eventProperty = {
      page_number: pageNumber,
      destination,
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logCalendlySessionBooked(): void {
    const event = Events.CALENDLY_SESSION_BOOKED
    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
    })
  }

  logSupportEmailPressed(): void {
    const event = Events.SUPPORT_EMAIL_PRESSED
    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
    })
  }

  logSupportBtnClicked({ event }: { event: Events }): void {
    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
    })
  }

  logPageShown({
    pageName,
    email,
    phone,
  }: {
    pageName: string
    email?: string
    phone?: string
  }): void {
    const event = Events.PAGE_SHOWED
    const eventProperty = {
      page_name: pageName,
      email,
      phone_number: phone,
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty, isPageEvent: true })
  }

  logRoverPageShow(): void {
    const event = Events.ROVER_PAGE_SHOW
    this.logEventOrPushToQueue({ event, eventProperty: this.commonEventProps })
  }

  logClickCopyCode(): void {
    const event = Events.CLICK_COPY_CODE
    this.logEventOrPushToQueue({ event, eventProperty: this.commonEventProps })
  }

  logBrandLogoClick(): void {
    const event = Events.BRAND_LOGO_CLICK
    this.logEventOrPushToQueue({ event, eventProperty: this.commonEventProps })
  }

  logRoverPageCompleted({
    action,
    promocode,
  }: {
    action: 'find_trainer' | 'skip' | 'copy_my_code'
    promocode: string
  }): void {
    const event = Events.ROVER_PAGE_COMPLETED
    const eventProperty = {
      action,
      promocode,
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logWoofzChatTap({
    pageName,
    cb,
  }: {
    pageName: string
    cb?: () => void
  }): void {
    const event = Events.WOOFZ_CHAT_TAP
    const eventProperty = {
      page_name: pageName,
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty, isPageEvent: true, cb })
  }

  logWoofyChatOpened({
    leadId,
    sessionId,
    coachName,
  }: {
    leadId: string
    sessionId: string
    coachName?: string
  }): void {
    const event = Events.CHAT_OPENED
    const eventProperty = {
      dog_id: leadId,
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      ...(coachName ? { coach_name: coachName } : {}),
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logWoofyLoaderShow({
    leadId,
    cohortLeadId,
  }: {
    leadId: string
    cohortLeadId: string
  }): void {
    const event = Events.WOOFY_LOADER_SHOW
    const eventProperty = {
      dog_id: leadId,
      session_id: cohortLeadId,
      timestamp: new Date().toISOString(),
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logCoachScreenShow({
    leadId,
    sessionId,
  }: {
    leadId: string
    sessionId: string
  }): void {
    const event = Events.COACH_SCREEN_SHOW
    const eventProperty = {
      dog_id: leadId,
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logCoachOptionTapped({
    leadId,
    sessionId,
    coachName,
  }: {
    leadId: string
    sessionId: string
    coachName: string
  }): void {
    const event = Events.COACH_OPTION_TAPPED
    const eventProperty = {
      coach_name: coachName,
      dog_id: leadId,
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logWoofyChatClosed({
    leadId,
    sessionId,
    closeMethod,
  }: {
    leadId: string
    sessionId: string
    closeMethod: 'auto' | 'manual'
  }): void {
    const event = Events.CHAT_CLOSED
    const eventProperty = {
      dog_id: leadId,
      session_id: sessionId,
      close_method: closeMethod,
      timestamp: new Date().toISOString(),
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logCloseAttemptedPopupShow({
    leadId,
    cohortLeadId,
  }: {
    leadId: string
    cohortLeadId: string
  }): void {
    const event = Events.CLOSE_ATTEMPTED_POPUP_SHOW
    const eventProperty = {
      dog_id: leadId,
      session_id: cohortLeadId,
      timestamp: new Date().toISOString(),
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({
      event,
      eventProperty,
    })
  }

  logCloseAttemptedPopupClose({
    leadId,
    cohortLeadId,
  }: {
    leadId: string
    cohortLeadId: string
  }): void {
    const event = Events.CLOSE_ATTEMPTED_POPUP_CLOSE
    const eventProperty = {
      dog_id: leadId,
      session_id: cohortLeadId,
      timestamp: new Date().toISOString(),
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({
      event,
      eventProperty,
    })
  }

  logZendeskChatShown({ paymentError }: { paymentError: string }): void {
    const event = Events.ZENDESK_CHAT_SHOWN
    this.logEventOrPushToQueue({
      event,
      eventProperty: { payment_error: paymentError, ...this.commonEventProps },
    })
  }

  logZendeskChatMsgSent({ paymentError }: { paymentError: string }): void {
    const event = Events.ZENDESK_CHAT_MSG_SENT
    this.logEventOrPushToQueue({
      event,
      eventProperty: { payment_error: paymentError, ...this.commonEventProps },
    })
  }

  logFbcSegmentActivated({
    abVariant,
    abSegmentName,
  }: {
    abVariant: string | string[]
    abSegmentName: string | string[]
  }): void {
    const event = Events.SEGMENT_ACTIVATED
    const eventProperty = {
      ab_variant: abVariant,
      ab_segment_name: abSegmentName,
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({
      event,
      eventProperty,
      includedLoggersNames: [EventLoggerInstanceName.RUDDERSTACK],
    })
  }

  // Sale Page Events
  logSalePageShown({
    productIds,
    email,
    hasEmailConsent,
    pageNumber,
    trialPrice,
    screenName,
  }: {
    productIds?: string[]
    email: string
    hasEmailConsent?: boolean
    trialPrice?: number
    pageNumber?: number
    screenName?: ScreenName
  }): void {
    const event = Events.SALE_SCREEN_SHOW
    const eventProperty = {
      ...(productIds && { product_id: productIds.join(',') }),
      screen_name: screenName || ScreenName.SALES,
      email,
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      intro_price: trialPrice,
      ...(pageNumber !== undefined && { page_number: pageNumber }),
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty, isPageEvent: true })
  }

  // EmailWrapper Page Events
  logEmailPageShown(): void {
    const event = Events.EMAIL_PAGE_SHOW

    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
      isPageEvent: true,
    })
  }

  logEmailPageCompleted({
    email,
    hasEmailConsent,
  }: {
    email: string
    hasEmailConsent: boolean
  }): void {
    const event = Events.EMAIL_PAGE_COMPLETED
    const eventProperty = {
      email,
      email_consent: hasEmailConsent,
      ...this.commonEventProps,
    }

    this.updateUserPropertiesByLogger({
      loggersNames: [
        EventLoggerInstanceName.AMPLITUDE,
        EventLoggerInstanceName.RUDDERSTACK,
      ],
      options: {
        email,
        email_consent: hasEmailConsent,
      },
    })
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logEmailPageError(eventProperty: { error: string }): void {
    const event = Events.EMAIL_PAGE_ERROR
    this.logEventOrPushToQueue({
      event,
      eventProperty: { eventProperty, ...this.commonEventProps },
    })
  }

  // Account Page events
  logCreateAccountShown(): void {
    const event = Events.CREATE_ACCOUNT_PAGE

    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
      isPageEvent: true,
    })
  }

  logLoginMethodSelected({ method }: { method: LoginMethod }): void {
    const event = Events.LOGIN_METHOD_SELECTED
    const eventProperty = {
      method,
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logAccountCreated({
    method,
    token,
    email,
  }: {
    method: LoginMethod | null
    token: string
    email: string | null
  }): void {
    const event = Events.ACCOUNT_CREATED
    const eventProperty = {
      method,
      token,
      email,
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logAccountCreationFailed({ error }: { error: string }): void {
    const event = Events.ACCOUNT_CREATION_FAILED
    const eventProperty = {
      error_reason: error,
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  // Getting App Page Events
  logGettingAppShown(): void {
    const event = Events.FINISH_ACCOUNT_SCREEN_VIEW

    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
      isPageEvent: true,
    })
  }

  logDownloadClicked({
    cb,
    productName,
  }: {
    cb?: () => void
    productName?: string
  }): void {
    const event = Events.DOWNLOAD_BTN_PRESSED
    const eventProperty = {
      ...(productName && { product_name: productName }),
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty, cb })
  }

  logWebAppRedirectClicked({ cb }: { cb?: () => void }): void {
    const event = Events.WEBAPP_BTN_PRESSED

    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
      cb,
    })
  }

  // Payment
  logPaymentMethodPageShown({
    email,
    screenName,
    hasEmailConsent,
  }: {
    email: string
    hasEmailConsent?: boolean
    screenName: string
  }): void {
    const event = Events.PAYMENT_METHOD_PAGE_SHOW
    const eventProperty = {
      screen_name: screenName,
      email,
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty, isPageEvent: true })
  }

  logEmailPopupShown(): void {
    const event = Events.EMAIL_POPUP_SHOW
    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
    })
  }

  logEmailExtraScreenShown(): void {
    const event = Events.EMAIL_EXTRA_SCREEN_SHOW
    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
      isPageEvent: true,
    })
  }

  logTooltipOpened(): void {
    const event = Events.WS_TOOLTIP_OPND
    this.logEventOrPushToQueue({
      event,
      eventProperty: { ...this.commonEventProps },
    })
  }

  logPaymentMethodPageClose({
    email,
    productId,
    productName,
    screenName,
    hasEmailConsent,
  }: {
    email: string
    hasEmailConsent?: boolean
    productId?: string
    productName: string
    screenName: string
  }): void {
    const event = Events.SUBS_PURCHASE_CLOSE
    const eventProperty = {
      product_name: productName,
      email,
      screen_name: screenName,
      ...(productId && { product_id: productId }),
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  // SubscriptionsWrapper Page events
  logPurchaseShown({
    productName,
    productId,
    screenName,
    email,
    hasEmailConsent,
  }: {
    productName: string
    productId?: string
    screenName?: ScreenName
    email: string
    hasEmailConsent?: boolean
  }): void {
    const event = Events.PURCHASE_SHOW
    const eventProperty = {
      product_name: productName,
      screen_name: screenName || ScreenName.SALES,
      email,
      ...(productId && { product_id: productId }),
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty, isPageEvent: true })
  }

  logSubscriptionsViewPrice({
    method,
    screenName,
    email,
    hasEmailConsent,
    scrollLvl,
  }: {
    method: string
    screenName: string
    email: string
    hasEmailConsent?: boolean
    scrollLvl?: string
  }): void {
    const event = Events.VIEW_PRICE
    const eventProperty = {
      method,
      screen_name: screenName,
      email,
      scroll_lvl: scrollLvl,
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logPressUpsellTooltip({ productName }: { productName: string }): void {
    const event = Events.PRESS_UPSELL_TOOLTIP
    const eventProperty = {
      product_name: productName,
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logPurchaseStarted({
    productId,
    productName,
    priceDetails: { price, trial = false, currency = 'USD' },
    paymentMethod,
    email,
    hasEmailConsent,
    screenName,
    stripeAccount,
  }: {
    productId: string
    productName: string
    priceDetails: {
      price: number
      trial?: boolean
      currency?: string
    }
    paymentMethod?: string
    email: string
    hasEmailConsent?: boolean
    screenName?: ScreenName | null
    stripeAccount?: IStripeAccount
  }): void {
    const event = Events.PURCHASE_STARTED
    const commonProps = {
      ...this.commonEventProps,
      attribution: {
        ...(this.commonEventProps?.attribution ?? {}),
        ...(this.campaignId && { campaign_id: this.campaignId }),
      },
    }

    const eventProperty = {
      trial,
      price,
      currency,
      email,
      product_id: productId,
      product_name: productName,
      screen_name: screenName || ScreenName.SALES,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      payment_system:
        paymentMethod === PaymentMethod.PAYPAL
          ? PaymentSystem.PAYPAL
          : PaymentSystem.STRIPE,
      ...(paymentMethod !== PaymentMethod.PAYPAL &&
        stripeAccount && {
          stripe_account_id: stripeAccount.accountId,
          stripe_account_name: stripeAccount.accountName,
        }),
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      ...commonProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logPurchaseCompleted({
    productId,
    productName,
    priceDetails: { price, trialPrice, trial = false, currency },
    paymentMethod,
    discountApplied,
    transactionId,
    email,
    hasEmailConsent,
    screenName,
    isUpsell,
    productPrice,
    stripeAccount,
    abVariant,
    abSegmentName,
    invoiceNumber,
    appliedCouponId,
    finalAmount,
    billingPeriod,
    nextBillingDate,
  }: {
    productId: string
    productName: string
    priceDetails: {
      price: number
      trialPrice?: number
      trial?: boolean
      currency?: string
    }
    paymentMethod?: string
    discountApplied?: string
    transactionId?: string
    email: string
    hasEmailConsent?: boolean
    screenName?: ScreenName | null
    isUpsell?: boolean
    productPrice: string
    stripeAccount?: IStripeAccount
    abVariant?: string | string[]
    abSegmentName?: Record<string, string> | string | string[] | null
    invoiceNumber?: string
    appliedCouponId?: string
    finalAmount?: number
    billingPeriod?: string
    nextBillingDate?: string
  }): void {
    const event = isUpsell
      ? Events.INAPP_PURCHASE_COMPLETED
      : Events.PURCHASE_COMPLETED

    const date = new Date()
    const formattedDate = `${date.toISOString().split('.')[0]}Z`
    const commonProps = {
      ...this.commonEventProps,
      attribution: {
        ...(this.commonEventProps?.attribution ?? {}),
        ...(this.campaignId && { campaign_id: this.campaignId }),
      },
    }

    const introPrice =
      productName === 'chat_assistant' && screenName === ScreenName.CHAT_UPSELL
        ? 0
        : trialPrice || price

    const eventProperty = {
      trial,
      price,
      currency,
      email,
      product_name: productName,
      product_id: productId,
      screen_name: screenName || ScreenName.SALES,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      product_price: productPrice,
      purchase_date: formattedDate,
      invoice_number: invoiceNumber,
      payment_system:
        paymentMethod === PaymentMethod.PAYPAL
          ? PaymentSystem.PAYPAL
          : PaymentSystem.STRIPE,
      ...(paymentMethod !== PaymentMethod.PAYPAL &&
        stripeAccount && {
          stripe_account_id: stripeAccount.accountId,
          stripe_account_name: stripeAccount.accountName,
        }),
      ...(transactionId && { transaction_id: transactionId }),
      ...(discountApplied && { discount_applied: discountApplied }),
      ...(abVariant && { ab_variant: abVariant }),
      ...(abSegmentName && { ab_segment_name: abSegmentName }),
      ...(billingPeriod && { billing_period: billingPeriod }),
      ...(nextBillingDate && { next_billing_date: nextBillingDate }),
      ...(!isUpsell && {
        intro_price: introPrice,
        coupon: appliedCouponId || 'none',
        charge_amount:
          finalAmount !== null && finalAmount !== undefined
            ? (finalAmount / 100).toFixed(2)
            : introPrice.toFixed(2),
      }),
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      ...commonProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logPurchaseFailed({
    productId,
    productName,
    priceDetails: { price, trial = false, currency = 'USD' },
    error: { description, type, code, declineCode },
    paymentMethod,
    screenName,
    stripeAccount,
    email,
    hasEmailConsent,
    isRetry,
  }: {
    productId: string
    productName: string
    priceDetails: {
      price: number
      trial?: boolean
      currency?: string
    }
    error: {
      type: string
      description?: string
      code?: string
      declineCode?: string
    }
    paymentMethod?: string
    screenName?: ScreenName | null
    stripeAccount?: IStripeAccount
    email: string
    hasEmailConsent?: boolean
    isRetry: boolean
  }): void {
    const event = Events.PURCHASE_FAILED
    const eventProperty = {
      trial,
      price,
      currency,
      email,
      ...(hasEmailConsent !== undefined && { email_consent: hasEmailConsent }),
      is_retry: isRetry,
      error_type: type,
      ...(description && { error_description: description }),
      ...(code && { error_code: code }),
      ...(declineCode && { decline_code: declineCode }),
      product_id: productId,
      product_name: productName,
      screen_name: screenName || ScreenName.SALES,
      payment_method: paymentMethod || PaymentMethod.CREDIT_CARD,
      payment_system:
        paymentMethod === PaymentMethod.PAYPAL
          ? PaymentSystem.PAYPAL
          : PaymentSystem.STRIPE,
      ...(paymentMethod !== PaymentMethod.PAYPAL &&
        stripeAccount && {
          stripe_account_id: stripeAccount.accountId,
          stripe_account_name: stripeAccount.accountName,
        }),
      ...this.commonEventProps,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logProcessingPurchaseShow({ email }: { email?: string }): void {
    const event = Events.PROCESSING_PURCHASE_SHOW
    const eventProperty = {
      email,
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logSuccessPurchaseShow({
    email,
    screenName,
  }: {
    email?: string
    screenName?: string
  }): void {
    const event = Events.SUCCESS_PURCHASE_SHOW
    const eventProperty = {
      email,
      screen_name: screenName || 'success',
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  logSuccessPurchaseClosed({ closedMethod }: { closedMethod?: string }): void {
    const event = Events.SUCCESS_PURCHASE_CLOSED
    const eventProperty = {
      closed_method: closedMethod || 'continue_click',
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({ event, eventProperty })
  }

  updateUserPropertiesByLogger({
    loggersNames,
    options,
  }: {
    loggersNames: EventLoggerInstanceName[]
    options: Record<string, any>
  }): void {
    loggersNames.forEach((loggerName) => {
      const logger = this.loggers?.get(loggerName)
      logger?.updateUserProperties && logger.updateUserProperties(options)
    })
  }

  private logEventOrPushToQueue(value: IEventLogParams): void {
    if (this.loggers?.size) {
      this.logEvent(value)
    } else {
      this.eventsQueue.push(value)
    }
  }

  private executeAndClearEventsQueue() {
    if (this.eventsQueue.length) {
      this.eventsQueue.forEach((value) => this.logEvent(value))
      this.eventsQueue = []
    }
  }

  private logEvent(value: IEventLogParams): void {
    this.loggers?.forEach((logger) => {
      if (value.includedLoggersNames?.length) {
        value.includedLoggersNames.includes(logger.name) && logger.log(value)
        return
      }

      logger.log(value)
    })
  }
}

export const eventLogger = new EventLoggerService()
